import moment from "moment-timezone";
import { getKitchenTimezone } from "./timezone";

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const WEEKDAY_ALIASES = {
  sun: "Sunday",
  sunday: "Sunday",
  mon: "Monday",
  monday: "Monday",
  tue: "Tuesday",
  tues: "Tuesday",
  tuesday: "Tuesday",
  wed: "Wednesday",
  weds: "Wednesday",
  wednesday: "Wednesday",
  thu: "Thursday",
  thur: "Thursday",
  thurs: "Thursday",
  thursday: "Thursday",
  fri: "Friday",
  friday: "Friday",
  sat: "Saturday",
  saturday: "Saturday",
};

const TIME_FORMATS = [
  "hh:mmA",
  "h:mmA",
  "hh:mm A",
  "h:mm A",
  "HH:mm",
  "H:mm",
];

const normalizeSchedule = schedule => {
  if (!schedule) {
    return [];
  }
  if (Array.isArray(schedule)) {
    return schedule;
  }
  if (Array.isArray(schedule.schedule)) {
    return schedule.schedule;
  }
  return [];
};

const resolveDayName = raw => {
  if (raw == null) {
    return null;
  }
  const cleaned = String(raw).trim().toLowerCase();
  if (!cleaned) {
    return null;
  }
  if (WEEKDAY_ALIASES[cleaned]) {
    return WEEKDAY_ALIASES[cleaned];
  }
  return WEEKDAYS.find(day => day.toLowerCase() === cleaned) || null;
};

/** Expand a schedule key like "Monday, Tuesday" or "Monday - Friday" into day names. */
const expandKeyToDays = key => {
  const days = new Set();
  const segments = String(key)
    .split(",")
    .map(segment => segment.trim())
    .filter(Boolean);

  for (const segment of segments) {
    const rangeParts = segment.split(/\s*[-–—]\s*/).map(part => part.trim());
    if (rangeParts.length === 2) {
      const start = resolveDayName(rangeParts[0]);
      const end = resolveDayName(rangeParts[1]);
      if (start && end) {
        let index = WEEKDAYS.indexOf(start);
        const endIndex = WEEKDAYS.indexOf(end);
        for (let step = 0; step < 7; step += 1) {
          days.add(WEEKDAYS[index]);
          if (index === endIndex) {
            break;
          }
          index = (index + 1) % 7;
        }
        continue;
      }
    }

    const day = resolveDayName(segment);
    if (day) {
      days.add(day);
    }
  }

  return days;
};

const entryIncludesDay = (entry, dayName) =>
  Object.keys(entry || {}).some(key => expandKeyToDays(key).has(dayName));

const hasTimeWindow = hours =>
  Boolean(
    hours &&
      ((hours.from && hours.to && hours.from !== "" && hours.to !== "") ||
        (hours.from2 && hours.to2 && hours.from2 !== "" && hours.to2 !== ""))
  );

/**
 * Resolve today's hours from a schedule entry.
 * Grouped multi-day rows in the sidebar use Object.values(entry)[0], so if today's
 * key is present but empty we fall back to the first populated window.
 */
const getHoursForDay = (entry, dayName) => {
  if (!entry || typeof entry !== "object") {
    return null;
  }

  for (const [key, hours] of Object.entries(entry)) {
    if (resolveDayName(key) === dayName && hasTimeWindow(hours)) {
      return hours;
    }
  }

  for (const [key, hours] of Object.entries(entry)) {
    if (expandKeyToDays(key).has(dayName) && hasTimeWindow(hours)) {
      return hours;
    }
  }

  if (entryIncludesDay(entry, dayName)) {
    for (const hours of Object.values(entry)) {
      if (hasTimeWindow(hours)) {
        return hours;
      }
    }
  }

  return null;
};

const parseScheduleTime = (value, timezone) => {
  if (value == null || value === "") {
    return null;
  }
  const tz = getKitchenTimezone(timezone);
  const raw = String(value).trim();
  const strict = moment.tz(raw, TIME_FORMATS, true, tz);
  if (strict.isValid()) {
    return strict;
  }
  const loose = moment.tz(raw, TIME_FORMATS, tz);
  return loose.isValid() ? loose : null;
};

const isWithinWindow = (from, to, now, timezone) => {
  const start = parseScheduleTime(from, timezone);
  const end = parseScheduleTime(to, timezone);
  if (!start || !end) {
    return false;
  }

  // Overnight window (e.g. 5:00PM - 2:00AM).
  if (end.isSameOrBefore(start)) {
    return now.isSameOrAfter(start) || now.isSameOrBefore(end);
  }

  return now.isBetween(start, end, null, "[]");
};

const dayHoursAreOpen = (dayHours, now, timezone) =>
  Boolean(
    dayHours &&
      (isWithinWindow(dayHours.from, dayHours.to, now, timezone) ||
        isWithinWindow(dayHours.from2, dayHours.to2, now, timezone))
  );

export const businessOpenCheck = (schedule, timezone) => {
  const entries = normalizeSchedule(schedule);
  if (entries.length === 0) {
    return false;
  }

  const tz = getKitchenTimezone(timezone);
  const now = moment.tz(tz);
  const dow = now.format("dddd");

  return entries.some(entry =>
    dayHoursAreOpen(getHoursForDay(entry, dow), now, tz)
  );
};

export const getClosingTimeIncrements = (schedule, waitTime, timezone) => {
  const entries = normalizeSchedule(schedule);
  if (entries.length === 0) {
    return null;
  }
  const tz = getKitchenTimezone(timezone);
  const dow = moment.tz(tz).format("dddd");
  let closingTime = null;
  let increments = [];
  const now = moment.tz(tz).add(waitTime, "minutes");

  entries.forEach(obj => {
    const dayHours = getHoursForDay(obj, dow);
    if (dayHours?.to) {
      closingTime = parseScheduleTime(dayHours.to, tz);
    }
  });

  // Calculate the difference in minutes between now and closing time
  const diffInMinutes = moment(closingTime).diff(now, "minutes");
  // Round down to the nearest 15-minute interval
  const roundedDiff = Math.ceil(diffInMinutes / waitTime) * waitTime;
  // Generate time increments starting from now, in 15-minute intervals
  for (let i = 0; i <= roundedDiff; i += waitTime) {
    const time = now.clone().add(i, "minutes");
    if (time.isBefore(closingTime)) {
      const minute = time.minute();
      const roundedMinute = Math.ceil(minute / 5) * 5;
      time.minute(roundedMinute);
      increments.push(time.format("hh:mmA"));
    }
  }

  return increments;
};

export const generateAvailableTimeSlots = (
  schedule,
  dayOffset,
  waitTime,
  timezone
) => {
  const entries = normalizeSchedule(schedule);
  const tz = getKitchenTimezone(timezone);
  const todayIndex = moment.tz(tz).isoWeekday();
  const targetIndex = (todayIndex + dayOffset) % 7;
  const dayName = moment.tz(tz).day(targetIndex).format("dddd");

  const dayHours = entries
    .map(obj => getHoursForDay(obj, dayName))
    .find(hours => hours);
  if (!dayHours) return { increments: [], dayName, openingTime: null };

  let closingTime = parseScheduleTime(dayHours.to, tz);
  let openingTime = parseScheduleTime(dayHours.from, tz);
  if (!closingTime || !openingTime) {
    return { increments: [], dayName, openingTime: null };
  }
  const now = moment.tz(tz).add(waitTime, "minutes");

  if (dayOffset === 0) {
    openingTime = now.isAfter(openingTime) ? now.clone() : openingTime;
  }

  const minute = openingTime.minute();
  const roundedMinute = Math.ceil(minute / waitTime) * waitTime;
  openingTime.minute(roundedMinute).second(0);

  let increments = [];
  for (
    let time = openingTime.clone();
    time.isBefore(closingTime);
    time.add(waitTime, "minutes")
  ) {
    increments.push(time.format("hh:mmA"));
  }

  return { increments, dayName, openingTime: dayHours.from };
};

export const getOpeningOrClosingTime = (schedule, timezone) => {
  const entries = normalizeSchedule(schedule);
  const tz = getKitchenTimezone(timezone);
  const todayName = moment.tz(tz).format("dddd");
  const now = moment.tz(tz);
  const dayHours = entries
    .map(obj => getHoursForDay(obj, todayName))
    .find(hours => hours);

  if (!dayHours) return "Today";

  const { from, to } = dayHours;

  if (!from || !to) {
    return "";
  }

  const openingTime = parseScheduleTime(from, tz);
  const closingTime = parseScheduleTime(to, tz);

  if (!openingTime || !closingTime) {
    return "";
  }

  return now.isBetween(openingTime, closingTime, null, "[]")
    ? `${closingTime.format("hh:mmA")}`
    : `${openingTime.format("hh:mmA")}`;
};

//  Ne functions
export const findNextOpening = (schedule, timezone) => {
  const entries = normalizeSchedule(schedule);
  if (entries.length === 0) {
    return null;
  }
  const tz = getKitchenTimezone(timezone);
  const now = moment.tz(tz);
  const currentTime = now.format("hh:mmA");

  // Check next 7 days starting from today
  for (let i = 0; i < 7; i++) {
    const dayName = moment.tz(tz).add(i, "days").format("dddd");
    const dayHours = entries
      .map(obj => getHoursForDay(obj, dayName))
      .find(hours => hours);

    if (dayHours) {
      const openingTimes = [];

      // Add first opening/closing time pair if they exist
      if (dayHours.from && dayHours.to) {
        openingTimes.push({
          open: dayHours.from,
          close: dayHours.to,
        });
      }

      // Add second opening/closing time pair if they exist
      if (dayHours.from2 && dayHours.to2) {
        openingTimes.push({
          open: dayHours.from2,
          close: dayHours.to2,
        });
      }

      // Skip if no opening times
      if (openingTimes.length === 0) continue;

      // Sort times chronologically
      openingTimes.sort((a, b) =>
        moment(a.open, "hh:mmA").diff(moment(b.open, "hh:mmA"))
      );

      for (let timeSlot of openingTimes) {
        // For today, only return future times
          if (i === 0) {
            const current = moment(currentTime, "hh:mmA");
            const openTime = moment(timeSlot.open, "hh:mmA");
            const closeTime = moment(timeSlot.close, "hh:mmA");

            // If current time is before closing time and either:
            // 1. Current time is between opening and closing OR
            // 2. Current time is before opening (next available slot)
            if (
              current.isBefore(closeTime) &&
              (current.isBetween(openTime, closeTime, undefined, "[]") ||
                current.isBefore(openTime))
            ) {
              return {
                day: dayName,
                time: timeSlot.open,
                closingTime: timeSlot.close,
              };
            }
          } else {
          // For future days, return the first opening time
          return {
            day: dayName,
            time: timeSlot.open,
            closingTime: timeSlot.close,
          };
        }
      }
    }
  }
  return null;
};
